import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import { capture } from "@/lib/analytics";
import {
  Gift,
  Copy,
  Check,
  Mail,
  Sparkles,
  Clock,
  CheckCircle2,
  Send,
  Share2,
  Users,
  Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";

export default function Refer() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [friendEmail, setFriendEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/referral/me");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const refCode = data?.code || "";
  const shareUrl = useMemo(
    () => (refCode ? `https://aipromptdojo.com/api/share/${refCode}` : ""),
    [refCode]
  );
  const ogImageUrl = useMemo(
    () => (refCode ? `https://aipromptdojo.com/api/og/${refCode}.png` : ""),
    [refCode]
  );
  const shareMessage = useMemo(
    () =>
      `Hey — try PromptDojo with me. Five-minute lessons graded by real AI to level up your prompting. Use my code ${refCode} at sign-up and you both win: 90% off the first month of AI Mastery, or 30% off the first year.\n${shareUrl}`,
    [refCode, shareUrl]
  );

  // Annual discount countdown (live ticker)
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const annualExpiry = data?.annual_expires_at ? new Date(data.annual_expires_at).getTime() : null;
  const annualMsLeft = annualExpiry ? annualExpiry - now : null;
  const annualValid = !!(annualMsLeft && annualMsLeft > 0 && (data?.annual_credits || 0) > 0);
  const annualDaysLeft   = annualValid ? Math.floor(annualMsLeft / (24 * 3600 * 1000)) : 0;
  const annualHoursLeft  = annualValid ? Math.floor((annualMsLeft / (3600 * 1000)) % 24) : 0;
  const annualMinsLeft   = annualValid ? Math.floor((annualMsLeft / (60 * 1000)) % 60) : 0;

  // Emit `referral_annual_discount_expired` exactly once, the first time we detect
  // an expired annual credit on this dashboard view.
  const [expiredEmitted, setExpiredEmitted] = useState(false);
  useEffect(() => {
    if (expiredEmitted) return;
    if ((data?.annual_credits || 0) > 0 && annualExpiry && annualMsLeft !== null && annualMsLeft <= 0) {
      capture("referral_annual_discount_expired", { expired_at: data?.annual_expires_at });
      setExpiredEmitted(true);
    }
  }, [annualMsLeft, annualExpiry, data?.annual_credits, data?.annual_expires_at, expiredEmitted]);

  // Emit `referral_xp_awarded` once per session when the dashboard reports XP earned
  // from referrals (covers both fresh signups and re-visits after a referee paid).
  const [xpEmitted, setXpEmitted] = useState(false);
  useEffect(() => {
    if (xpEmitted) return;
    const xp = data?.referral_xp_total || 0;
    if (xp > 0) {
      capture("referral_xp_awarded", { xp_total: xp, referral_count: data?.referral_count || 0 });
      setXpEmitted(true);
    }
  }, [data?.referral_xp_total, data?.referral_count, xpEmitted]);

  if (loading || !data) {
    return (
      <div className="App min-h-screen">
        <Header />
        <main className="max-w-2xl mx-auto px-4 md:px-8 py-12 pb-28 md:pb-12 text-center">
          <span className="spinner" />
        </main>
      </div>
    );
  }

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(refCode);
      setCopiedCode(true);
      toast.success("Code copied. Now share it with a friend.");
      setTimeout(() => setCopiedCode(false), 1800);
    } catch {
      toast.error("Could not copy. Long-press and copy manually.");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      toast.success("Link copied.");
      setTimeout(() => setCopiedLink(false), 1800);
    } catch {
      toast.error("Could not copy. Long-press and copy manually.");
    }
  };

  const emailFriend = (e) => {
    e?.preventDefault();
    const to = friendEmail.trim();
    if (!to) {
      toast.error("Enter your friend's email first.");
      return;
    }
    const subject = encodeURIComponent("Try PromptDojo with me — 90% off Monthly or 30% off Annual");
    const body = encodeURIComponent(`${shareMessage}\n\nSee you in the dojo.`);
    capture("referral_sent", { channel: "email" });
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    setTimeout(() => setFriendEmail(""), 200);
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copyCode();
      capture("referral_sent", { channel: "copy_fallback" });
      return;
    }
    try {
      await navigator.share({
        title: "PromptDojo",
        text: shareMessage,
        url: shareUrl,
      });
      capture("referral_sent", { channel: "native_share" });
    } catch (e) {
      // user cancelled the native share sheet — expected, not an error.
      if (e?.name !== "AbortError") {
        // eslint-disable-next-line no-console
        console.warn("[refer] navigator.share failed:", e);
      }
    }
  };

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16 pb-28 md:pb-16">
        <span className="kicker">Referrals &amp; rewards</span>
        <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[1.02] tracking-tight">
          Invite a friend and <em>you both win.</em>
        </h1>
        <p className="mt-4 text-[var(--ink-2)] text-lg max-w-2xl leading-relaxed">
          Hand a friend your code. When they join AI Mastery, you both unlock:
        </p>
        <ul className="mt-3 space-y-1.5 text-[var(--ink-2)] text-base">
          <li className="flex items-start gap-2"><Check size={16} className="mt-0.5 text-[var(--emerald,#10B981)] flex-shrink-0" /> <span><strong className="text-[var(--ink)]">90% off</strong> AI Mastery Monthly — $0.99 instead of $9.99</span></li>
          <li className="flex items-start gap-2"><Check size={16} className="mt-0.5 text-[var(--emerald,#10B981)] flex-shrink-0" /> <span><strong className="text-[var(--ink)]">30% off</strong> AI Mastery Annual — $69.30 instead of $99</span></li>
          <li className="flex items-start gap-2"><Check size={16} className="mt-0.5 text-[var(--emerald,#10B981)] flex-shrink-0" /> <span>Annual discount expires in 30 days</span></li>
          <li className="flex items-start gap-2"><Check size={16} className="mt-0.5 text-[var(--emerald,#10B981)] flex-shrink-0" /> <span><strong className="text-[var(--ink)]">Bonus belt progress</strong> for every friend who joins</span></li>
        </ul>
        <p className="mt-3 text-sm text-[var(--ink-3)] max-w-2xl leading-relaxed">
          The more people you help gain an AI advantage, the more rewards you earn.
        </p>

        {/* CODE-FIRST share card */}
        <div className="brut-lg p-7 md:p-9 mt-8 bg-gradient-to-br from-white via-white to-[#f3fbf8]">
          <div className="flex items-center gap-2.5 mb-4">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "var(--emerald)" }}
            >
              <Gift size={16} color="#fff" />
            </div>
            <span className="kicker" style={{ color: "var(--emerald)" }}>
              Your referral code
            </span>
          </div>

          {/* Big code chip */}
          <button
            type="button"
            onClick={copyCode}
            className="group w-full rounded-2xl border-2 border-dashed border-[var(--line)] hover:border-[var(--emerald)] transition-colors p-6 md:p-8 flex items-center justify-between gap-4 bg-white"
            data-testid="refer-code-chip"
          >
            <div className="flex flex-col items-start min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-[var(--ink-3)] mb-2">
                Tap to copy
              </div>
              <div
                className="font-mono font-semibold text-3xl md:text-5xl tracking-[0.18em] text-[var(--ink)] truncate"
                data-testid="refer-code"
              >
                {refCode}
              </div>
            </div>
            <span
              className="btn-primary flex-shrink-0"
              style={{ padding: "0.6rem 1.1rem", fontSize: "0.85rem", pointerEvents: "none" }}
              data-testid="refer-copy-code-button"
            >
              {copiedCode ? (
                <>
                  <Check size={13} /> Copied
                </>
              ) : (
                <>
                  <Copy size={13} /> Copy code
                </>
              )}
            </span>
          </button>

          {/* Email a friend */}
          <form
            onSubmit={emailFriend}
            className="mt-5 flex gap-2 flex-wrap"
            data-testid="refer-email-form"
          >
            <div className="flex-1 min-w-[220px] flex items-center gap-2 rounded-xl border border-[var(--line)] bg-white px-3 py-2.5 focus-within:border-[var(--emerald)] transition-colors">
              <Mail size={15} className="text-[var(--ink-3)] flex-shrink-0" />
              <input
                type="email"
                value={friendEmail}
                onChange={(e) => setFriendEmail(e.target.value)}
                placeholder="friend@example.com"
                className="flex-1 bg-transparent outline-none text-sm text-[var(--ink)] placeholder:text-[var(--ink-3)]"
                data-testid="refer-friend-email-input"
              />
            </div>
            <button
              type="submit"
              className="btn-primary whitespace-nowrap"
              style={{ padding: "0.6rem 1.1rem", fontSize: "0.85rem" }}
              data-testid="refer-email-friend-button"
            >
              <Send size={13} /> Email friend
            </button>
            <button
              type="button"
              onClick={nativeShare}
              className="btn-ghost whitespace-nowrap"
              style={{ padding: "0.6rem 1.1rem", fontSize: "0.85rem" }}
              data-testid="refer-share-button"
            >
              <Share2 size={13} /> Share…
            </button>
          </form>

          {/* Link fallback (secondary) */}
          <div className="mt-5 pt-5 border-t border-[var(--line)]">
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ink-3)] mb-2 flex items-center gap-1.5">
              <LinkIcon size={11} /> Prefer a link?
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <code
                className="font-mono text-xs md:text-sm flex-1 min-w-0 break-all text-[var(--ink-2)]"
                data-testid="refer-share-url"
              >
                {shareUrl}
              </code>
              <button
                onClick={copyLink}
                className="btn-ghost"
                style={{ padding: "0.45rem 0.85rem", fontSize: "0.8rem" }}
                data-testid="refer-copy-link-button"
              >
                {copiedLink ? (
                  <>
                    <Check size={12} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={12} /> Copy link
                  </>
                )}
              </button>
            </div>
            <p className="text-[11px] text-[var(--ink-3)] mt-2">
              Pasted on Twitter, iMessage, or Slack? It auto-renders a share card with your belt rank.
            </p>
          </div>
        </div>

        {/* Belt-rank share card preview */}
        <div className="brut-lg p-6 md:p-7 mt-6" data-testid="refer-belt-card-block">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <span className="kicker">Your share card</span>
              <h3 className="font-display text-2xl mt-1 leading-tight">
                Flex your belt on social.
              </h3>
              <p className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed max-w-md">
                Any link you share renders this card on Twitter, iMessage, Slack & LinkedIn — auto-attributed to your referral code.
              </p>
            </div>
            <button
              onClick={copyLink}
              className="btn-primary whitespace-nowrap"
              style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}
              data-testid="refer-share-card-copy"
            >
              {copiedLink ? (
                <>
                  <Check size={13} /> Link copied
                </>
              ) : (
                <>
                  <Share2 size={13} /> Copy share link
                </>
              )}
            </button>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[var(--line)] bg-[#0F1419]">
            {ogImageUrl && (
              <img
                src={ogImageUrl}
                alt="Your belt rank share card"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="refer-og-preview"
              />
            )}
          </div>
        </div>

        {/* My Rewards — at-a-glance visibility */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="refer-rewards-grid">
          <Stat
            icon={Users}
            color="var(--primary,#2563EB)"
            label="Friends referred"
            value={data.referral_count || 0}
            sub={`${data.paid_referrals || 0} paid · ${data.pending_referrals || 0} pending`}
            testid="refer-stat-friends"
          />
          <Stat
            icon={Sparkles}
            color="var(--emerald)"
            label="90% off Monthly"
            value={data.monthly_credits || 0}
            sub={(data.monthly_credits || 0) > 0 ? "Ready to redeem at checkout" : "Earn 1 per paid referral"}
            testid="refer-stat-monthly"
          />
          <Stat
            icon={Gift}
            color="var(--accent)"
            label="30% off Annual"
            value={annualValid ? (data.annual_credits || 0) : 0}
            sub={annualValid
              ? `Expires in ${annualDaysLeft}d ${annualHoursLeft}h ${annualMinsLeft}m`
              : (data.annual_credits || 0) > 0
                ? "Expired — refer again to refresh"
                : "Earn 1 per paid referral"}
            testid="refer-stat-annual"
          />
          <Stat
            icon={CheckCircle2}
            color="var(--indigo,#6366F1)"
            label="Belt progress"
            value={data.referral_xp_total || 0}
            sub="Earned from referrals"
            testid="refer-stat-xp"
          />
        </div>

        {/* Achievement tiers */}
        {Array.isArray(data.achievements) && data.achievements.length > 0 && (
          <div className="mt-8" data-testid="refer-achievements">
            <h2 className="font-display text-2xl tracking-tight mb-3">
              Achievement tiers
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {data.achievements.map((a) => (
                <div
                  key={a.id}
                  className={`brut p-5 ${a.unlocked ? "border-[var(--emerald,#10B981)]" : ""}`}
                  data-testid={`refer-achievement-${a.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="kicker">{a.threshold === 1 ? "1 friend" : `${a.threshold} friends`}</span>
                    {a.unlocked ? (
                      <span className="pill pill-emerald" style={{ fontSize: "0.62rem", padding: "0.18rem 0.55rem" }}>
                        <Check size={11} /> Unlocked
                      </span>
                    ) : a.coming_soon ? (
                      <span className="pill" style={{ fontSize: "0.62rem", padding: "0.18rem 0.55rem", color: "var(--ink-3)" }}>
                        Coming Soon
                      </span>
                    ) : null}
                  </div>
                  <div className="font-display text-base leading-tight">{a.title}</div>
                  <p className="text-xs text-[var(--ink-2)] mt-1.5 leading-snug">{a.reward}</p>
                  {!a.unlocked && a.remaining > 0 && (
                    <p className="text-[11px] text-[var(--ink-3)] mt-2">
                      {a.remaining} {a.remaining === 1 ? "more friend" : "more friends"} to unlock
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active rewards banner */}
        {((data.monthly_credits || 0) > 0 || annualValid) && (
          <div
            className="brut p-5 mt-6 flex items-start gap-3"
            style={{
              background: "rgba(16,185,129,0.06)",
              borderColor: "rgba(16,185,129,0.30)",
            }}
            data-testid="refer-discount-banner"
          >
            <Sparkles
              size={18}
              className="mt-0.5 flex-shrink-0"
              style={{ color: "var(--emerald,#10B981)" }}
            />
            <div>
              <div className="font-medium text-[var(--ink)]">
                You have referral rewards ready
              </div>
              <div className="text-sm text-[var(--ink-2)] mt-1 leading-snug">
                {(data.monthly_credits || 0) > 0 && (
                  <>
                    <strong>{data.monthly_credits}</strong>× <strong>90% off Monthly</strong>
                    {annualValid ? " · " : ""}
                  </>
                )}
                {annualValid && (
                  <>
                    <strong>{data.annual_credits}</strong>× <strong>30% off Annual</strong>
                    {" "}(expires in {annualDaysLeft}d {annualHoursLeft}h)
                  </>
                )}
                . Redeem on the{" "}
                <Link to="/pricing" className="text-[var(--ink)] underline underline-offset-2">
                  Pricing page
                </Link>.
              </div>
            </div>
          </div>
        )}

        {/* If user was referred */}
        {data.referrer && !data.referral_first_used && (
          <div
            className="brut p-5 mt-6 flex items-start gap-3"
            data-testid="refer-referred-by-banner"
          >
            <Users size={18} className="mt-0.5 text-[var(--accent)] flex-shrink-0" />
            <div>
              <div className="font-medium text-[var(--ink)]">
                You were invited by {data.referrer.name}
              </div>
              <div className="text-sm text-[var(--ink-2)] mt-0.5">
                Pick AI Mastery and you'll get 90% off Monthly or 30% off Annual on checkout.
              </div>
            </div>
          </div>
        )}

        {/* History */}
        <div className="mt-10">
          <h2 className="font-display text-2xl tracking-tight mb-4">
            Your referrals
          </h2>
          {data.history.length === 0 ? (
            <div className="brut p-10 text-center text-[var(--ink-2)]">
              No referrals yet. Share your code above and watch this fill up.
            </div>
          ) : (
            <div className="brut overflow-hidden">
              {data.history.map((r, i) => (
                <div
                  key={`${r.email_masked || r.name}-${r.created_at || i}`}
                  className={`flex items-center justify-between p-4 ${i < data.history.length - 1 ? "border-b border-[var(--line)]" : ""}`}
                  data-testid={`refer-history-row-${i}`}
                >
                  <div className="flex items-center gap-3">
                    {r.picture ? (
                      <img
                        src={r.picture}
                        alt=""
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[var(--bg-2)] flex items-center justify-center text-xs font-medium text-[var(--ink-2)]">
                        {(r.name || "F").slice(0, 1)}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-[var(--ink)]">
                        {r.name}
                      </div>
                      <div className="text-xs text-[var(--ink-3)]">
                        {r.email_masked} ·{" "}
                        {new Date(r.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  {r.status === "paid" ? (
                    <span className="pill pill-emerald">
                      <Check size={11} /> Paid · you earned rewards
                    </span>
                  ) : (
                    <span className="pill">
                      <Clock size={11} /> Pending
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* How it works */}
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {[
            {
              n: "01",
              t: "Share your code",
              d: "Text it, email it, drop it in your group chat — it's short and easy to share.",
            },
            {
              n: "02",
              t: "They sign up & pay",
              d: "Friend redeems 90% off Monthly ($0.99) or 30% off Annual ($69.30) at checkout.",
            },
            {
              n: "03",
              t: "You both earn rewards",
              d: "Bonus belt progress and matching discounts in your account. Annual discount window is 30 days — refer again to refresh.",
            },
          ].map((s) => (
            <div key={s.n} className="brut p-6">
              <div className="text-sm font-medium text-[var(--accent)]">
                {s.n}
              </div>
              <div className="font-display text-2xl mt-2 leading-tight">
                {s.t}
              </div>
              <div className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
                {s.d}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, color, label, value, sub, testid }) {
  return (
    <div className="brut p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} style={{ color }} />
        <span className="kicker">{label}</span>
      </div>
      <div className="font-display text-5xl tracking-tight tabular-nums leading-none">
        {value}
      </div>
      <div className="text-xs text-[var(--ink-3)] mt-2 leading-snug">{sub}</div>
    </div>
  );
}
